/**
 * Client mirror of server fee calculation (display only — charges use server values).
 */

export const STRIPE_PERCENT = 0.029;
export const STRIPE_FIXED_DOLLARS = 0.30;
export const PAYMENT_PROCESSING_FEE_LABEL = 'Payment processing fee';
export const SALES_TAX_FEE_LABEL = 'Sales tax';

export const DEFAULT_PLATFORM_FEES = {
  standardFeePercentage: 5,
  importedCustomerFeePercentage: 1,
  renewalFeePercentage: 1,
};

export interface CheckoutBreakdown {
  servicePrice: number;
  salesTaxAmount: number;
  salesTaxPercent: number;
  salesTaxState: string | null;
  processingFee: number;
  amountBeforeProcessing: number;
  totalCharge: number;
  platformFeePercent: number;
  platformCut: number;
  providerCut: number;
}

function roundMoney(amount: number): number {
  return Math.round(amount * 100) / 100;
}

export function grossUpProcessingFee(netNeeded: number): { processingFee: number; totalCharge: number } {
  if (netNeeded <= 0) return { processingFee: 0, totalCharge: 0 };
  const totalCharge = roundMoney((netNeeded + STRIPE_FIXED_DOLLARS) / (1 - STRIPE_PERCENT));
  return { processingFee: roundMoney(totalCharge - netNeeded), totalCharge };
}

export function calculateServiceSplits(servicePrice: number, platformFeePercent: number) {
  const pct = platformFeePercent / 100;
  const platformCut = roundMoney(servicePrice * pct);
  return { platformCut, providerCut: roundMoney(servicePrice - platformCut) };
}

export interface CheckoutSalesTaxOptions {
  salesTaxPercent?: number | null;
  salesTaxState?: string | null;
}

export function calculateCheckoutBreakdown(
  servicePrice: number,
  platformFeePercent: number,
  salesTax?: CheckoutSalesTaxOptions
): CheckoutBreakdown {
  const price = roundMoney(servicePrice);
  const salesTaxPercent = Math.max(0, Number(salesTax?.salesTaxPercent) || 0);
  const salesTaxAmount = roundMoney(price * (salesTaxPercent / 100));
  const amountBeforeProcessing = roundMoney(price + salesTaxAmount);
  const { processingFee, totalCharge } = grossUpProcessingFee(amountBeforeProcessing);
  const { platformCut, providerCut: providerServiceCut } = calculateServiceSplits(price, platformFeePercent);
  const providerCut = roundMoney(providerServiceCut + salesTaxAmount);
  const salesTaxState = salesTax?.salesTaxState
    ? String(salesTax.salesTaxState).toUpperCase().slice(0, 2)
    : null;

  return {
    servicePrice: price,
    salesTaxAmount,
    salesTaxPercent,
    salesTaxState: salesTaxState && /^[A-Z]{2}$/.test(salesTaxState) ? salesTaxState : null,
    processingFee,
    amountBeforeProcessing,
    totalCharge,
    platformFeePercent,
    platformCut,
    providerCut,
  };
}

export function resolvePlatformFeePercent(
  tier: 'onboarded' | 'marketplace_first_season' | 'marketplace_renewal',
  settings?: Partial<typeof DEFAULT_PLATFORM_FEES>
): number {
  const s = { ...DEFAULT_PLATFORM_FEES, ...settings };
  if (tier === 'onboarded') return s.importedCustomerFeePercentage;
  if (tier === 'marketplace_renewal') return s.renewalFeePercentage;
  return s.standardFeePercentage;
}

export function resolveChargePlatformFeePercent(options: {
  bidPlatformFeePercentage?: number | null;
  isImportedCustomer?: boolean;
  settings?: Partial<typeof DEFAULT_PLATFORM_FEES>;
}): number {
  if (options.bidPlatformFeePercentage != null && !Number.isNaN(options.bidPlatformFeePercentage)) {
    return options.bidPlatformFeePercentage;
  }
  if (options.isImportedCustomer) {
    return resolvePlatformFeePercent('onboarded', options.settings);
  }
  return resolvePlatformFeePercent('marketplace_first_season', options.settings);
}

export function providerEarningsPercent(platformFeePercent: number): number {
  return roundMoney(100 - platformFeePercent);
}
